import { uiSurface } from "shared/ui/uiSurface"
import { Business, Person } from "@mui/icons-material"
import { Avatar, Chip, Stack, Tooltip, Typography } from "@mui/material"
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid"
import dayjs from "dayjs"
import { FC, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { ClientSummary } from "../../apis/appApi"
import { getAvatarColor, getInitials } from "./utils"

type CustomersGridProps = {
  rows: ClientSummary[]
  loading: boolean
  rowCount: number
  paginationModel: GridPaginationModel
  onPaginationModelChange: (model: GridPaginationModel) => void
  sortModel: GridSortModel
  onSortModelChange: (model: GridSortModel) => void
  onRowClick: (id: number) => void
}

export const CustomersGrid: FC<CustomersGridProps> = ({
  rows,
  loading,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  onRowClick,
}) => {
  const { t } = useTranslation()

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: t("clients.name"),
        flex: 1,
        minWidth: 220,
        valueGetter: (_value, row) =>
          `${row.firstName} ${row.lastName}`,
        renderCell: (params) => {
          const row = params.row
          const fullName = `${row.firstName} ${row.lastName}`
          const initials = getInitials(row.firstName, row.lastName)
          const color = getAvatarColor(fullName)
          return (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ height: "100%" }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: 13,
                  fontWeight: 600,
                  bgcolor: color,
                }}
              >
                {initials}
              </Avatar>
              <Typography variant="body2" noWrap>
                {fullName}
              </Typography>
            </Stack>
          )
        },
      },
      {
        field: "email",
        headerName: t("clients.email"),
        flex: 1,
        minWidth: 180,
        renderCell: (params) =>
          params.value ? (
            <Typography variant="body2">{params.value}</Typography>
          ) : (
            <Typography variant="body2" color="text.disabled">
              {"\u2014"}
            </Typography>
          ),
      },
      {
        field: "organisation",
        headerName: t("clients.organisation"),
        flex: 1,
        minWidth: 140,
        renderCell: (params) =>
          params.value ? (
            <Typography variant="body2">{params.value}</Typography>
          ) : (
            <Typography variant="body2" color="text.disabled">
              {"\u2014"}
            </Typography>
          ),
      },
      {
        field: "clientType",
        headerName: t("clients.type.label"),
        width: 130,
        sortable: false,
        valueGetter: (_value, row) => (row.organisation ? "business" : "individual"),
        renderCell: (params) => {
          const type = params.value as "business" | "individual"
          return (
            <Tooltip title={t(`clients.type.${type}Tooltip`)}>
              <Chip
                icon={
                  type === "business"
                    ? <Business sx={{ fontSize: 16 }} />
                    : <Person sx={{ fontSize: 16 }} />
                }
                label={t(`clients.type.${type}`)}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: type === "business" ? "#1976d2" : "#9e9e9e",
                  color: type === "business" ? "#1976d2" : "#616161",
                }}
              />
            </Tooltip>
          )
        },
      },
      {
        field: "archived",
        headerName: t("clients.status"),
        width: 110,
        renderCell: (params) => (
          <Chip
            label={params.value ? t("clients.archived") : t("clients.active")}
            size="small"
            sx={
              params.value
                ? { bgcolor: "#f5f5f5", color: "#757575" }
                : { bgcolor: "#e8f5e9", color: "#2e7d32" }
            }
          />
        ),
      },
      {
        field: "dateCreated",
        headerName: t("clients.dateCreated"),
        width: 140,
        renderCell: (params) => {
          const raw = params.value
          if (!raw) return <Typography variant="body2" color="text.disabled">{"\u2014"}</Typography>
          return (
            <Typography variant="body2">
              {dayjs(raw).format("MMM D, YYYY")}
            </Typography>
          )
        },
      },
    ],
    [t],
  )

  const handleRowClick = useCallback(
    (params: any) => {
      onRowClick(params.id as number)
    },
    [onRowClick],
  )

  return (
    <DataGrid
      {...uiSurface("UI-DLV2-082")}
      data-testid="customers-grid"
      rows={rows}
      columns={columns}
      loading={loading}
      rowCount={rowCount}
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      pageSizeOptions={[10, 25, 50]}
      onRowClick={handleRowClick}
      disableRowSelectionOnClick
      getRowClassName={(params) => `customer-row-${params.id}`}
      sx={{
        border: 0,
        height: "100%",
        "& .MuiDataGrid-row": { cursor: "pointer" },
      }}
    />
  )
}
